/**
 * ExamsGuide: the visual guide of the exam bank (/exams_list, /exams_create, /exams_list/:uuid), opened by the "?"
 * after the list's title and after the exam's name on its page (HelpTitle helpId="exams": both share the seen mark).
 * Written as BankGuide is: HelpKit blocks in their order, Mini pictures of the real controls, the sentences under
 * help.exams.* and every button named through its own i18n key.
 * It follows the screen: under 768px (the breakpoint of ViewExams' header and ExamsList's table) the exam page keeps
 * "Add test" and "⋯" in one row with "Generate Word" inside "⋯". Where there is a mouse (hover), a test row shows its
 * bin on the row the mouse is on; touch screens show it at once (ViewExams' own rule, whatever the width). Everyone
 * who opens the exam bank (create_exam) can do all of it, so there is no roles block; the legend says what students
 * see in each status (ExamStatus.js).
 * Props: `readOnly` (from an archived exam's page): that page has no "Add test"; phones show "Generate Word" beside
 * "⋯", wide screens "Generate Word" and "Excel for bilorg" as buttons ("⋯" would hold bilorg alone). The documents
 * step draws and says that. The other steps describe an exam that can be changed; the legend says an archived one
 * cannot.
 * "Generate Word" opens the «Word құжаты» dialog: it shows what the Word prints (its language and variant code, set
 * in the exam's settings, and each test's heading), and its own "Generate Word" makes two files, the key for the
 * teacher and the student copy, each downloaded with its own button. The documents step draws that (the dialog, then
 * the two files, the student copy marked: the one to print) and says where the language and the code are set.
 * The grip is the app's GripIcon, the test rows' own handle, drawn as a MiniButton's icon.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  ArrowsUpDownIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  InformationCircleIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TableCellsIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { HelpFlow, HelpIntro, HelpLegend, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniCheck,
  MiniDialog,
  MiniField,
  MiniIcon,
  MiniMenu,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniText,
} from "../Mini";
import GripIcon from "../../Icons/GripIcon";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";

export default function ExamsGuide({ readOnly = false }) {
  const [t] = useTranslation();
  // The exam bank's own breakpoint: ViewExams' header actions and test rows, ExamsList's table and cards.
  const wide = useMediaQuery("(min-width: 768px)");
  // A mouse: the test rows show their bin on hover; a touch screen shows it on every row.
  const hover = useMediaQuery("(hover: hover)");
  // Phones keep Word in "⋯" beside Add test; an archived exam's page (no Add test) shows Word itself.
  const wordInMenu = !wide && !readOnly;
  // An archived exam on a wide screen: Excel for bilorg is a button beside Word, there is no "⋯".
  const bilorgButton = wide && readOnly;
  const ex = (key) => t(`help.exams.example.${key}`);
  const exam = ex("exam");

  // The labels of the real controls.
  const create = t("exam_bank.list.create_exam");
  const add = t("exam_bank.view.add_test");
  const word = t("exam_bank.view.word_generate");
  const bilorg = t("exam_bank.view.bilorg_export");
  const editExam = t("exam_bank.view.edit_exam");
  const deleteExam = t("exam_bank.list.delete_exam");
  const activate = t("exam_bank.status_actions.active");
  const archive = t("exam_bank.status_actions.archived");
  const toDraft = t("exam_bank.status_actions.draft");
  const available = t("nav.available_exams");
  const dialogTitle = t("exam_bank.word.title");
  const keyFile = t("exam_bank.word.download_key");
  const studentFile = t("exam_bank.word.download_student");

  // The exam page's header: its name and status, and its actions as the reader's screen has them (wide: Word
  // quiet, Add test, "⋯"; phones: Add test and "⋯", Word inside "⋯"; an archived exam: no Add test, wide screens
  // show Word and bilorg, phones Word beside "⋯"). `marks` puts a marker on word, add or menu.
  const examHeader = (marks = {}, { archived = false } = {}) => (
    <MiniRow className="justify-between gap-y-3">
      <MiniStack tight>
        <MiniRow>
          <MiniText variant="title">{exam}</MiniText>
        </MiniRow>
        <MiniRow>
          {archived ? (
            <MiniBadge tone="slate">{t("exam_bank.status.archived")}</MiniBadge>
          ) : (
            <MiniBadge tone="amber">{t("exam_bank.status.draft")}</MiniBadge>
          )}
        </MiniRow>
      </MiniStack>
      {wide ? (
        <MiniRow>
          <MiniButton variant="quiet" icon={DocumentTextIcon} mark={marks.word}>
            {word}
          </MiniButton>
          {archived ? (
            <MiniButton variant="quiet" icon={TableCellsIcon}>
              {bilorg}
            </MiniButton>
          ) : (
            <>
              <MiniButton variant="primary" icon={PlusIcon} mark={marks.add}>
                {add}
              </MiniButton>
              <MiniButton variant="quiet" icon={EllipsisHorizontalIcon} mark={marks.menu} />
            </>
          )}
        </MiniRow>
      ) : (
        <MiniRow className="flex-nowrap">
          {archived ? (
            <MiniButton variant="outline" icon={DocumentTextIcon} mark={marks.word}>
              {word}
            </MiniButton>
          ) : (
            <MiniButton variant="primary" icon={PlusIcon} mark={marks.add}>
              {add}
            </MiniButton>
          )}
          <MiniButton variant="outline" icon={EllipsisHorizontalIcon} mark={marks.menu} />
        </MiniRow>
      )}
    </MiniRow>
  );

  // A draft's "⋯" menu, opened under its button, as the reader's screen has it (phones: Word first; then Excel for
  // bilorg, the settings and the red delete). `marks` puts a marker on word or edit.
  const draftMenu = (marks = {}) => (
    <MiniRow className="justify-end">
      <MiniMenu
        items={[
          !wide && { label: word, icon: DocumentTextIcon, mark: marks.word },
          { label: bilorg, icon: TableCellsIcon },
          { label: editExam, icon: PencilIcon, mark: marks.edit },
          { label: deleteExam, icon: TrashIcon, danger: true },
        ]}
      />
    </MiniRow>
  );

  // The «Word құжаты» dialog as it opens: the Word's language and variant code (a Kazakh exam's footer phrase), a
  // test as it prints (its heading in capitals) and its "Generate Word" (marked `mark`).
  const wordDialog = (mark) => (
    <MiniDialog
      title={dialogTitle}
      footer={[
        <MiniButton key="close" variant="outline">
          {t("buttons.close")}
        </MiniButton>,
        <MiniButton key="make" variant="primary" mark={mark}>
          {word}
        </MiniButton>,
      ]}
    >
      <MiniRow>
        <MiniText variant="muted">{t("exam_bank.word.language")}</MiniText>
        <MiniText variant="strong">{t("exam_bank.word.language_kk")}</MiniText>
      </MiniRow>
      <MiniRow>
        <MiniText variant="muted">{t("exam_bank.word.code")}</MiniText>
        <MiniText variant="strong">3</MiniText>
        <MiniText variant="muted">{t("exam_bank.word.code_footer", { phrase: "НҰСҚА – 3" })}</MiniText>
      </MiniRow>
      <MiniCard>
        <MiniRow className="justify-between">
          <MiniRow>
            <MiniBadge tone="blue">1</MiniBadge>
            <MiniText variant="strong">{t("help.common.example.subject").toLocaleUpperCase()}</MiniText>
          </MiniRow>
          <MiniText variant="muted">{t("exam_bank.word.questions", { count: 30 })}</MiniText>
        </MiniRow>
      </MiniCard>
    </MiniDialog>
  );

  // The two files once made, each with its own download: the key for the teacher and the student copy (marked
  // `mark`: the one to print).
  const wordFiles = (mark) => (
    <MiniCard icon={<MiniIcon icon={CheckCircleIcon} tone="green" />}>
      <MiniText variant="strong">{t("exam_bank.word.ready_title")}</MiniText>
      <MiniRow className="gap-y-3">
        <MiniButton variant="outline" icon={ArrowDownTrayIcon}>
          {keyFile}
        </MiniButton>
        <MiniButton variant="outline" icon={ArrowDownTrayIcon} mark={mark}>
          {studentFile}
        </MiniButton>
      </MiniRow>
    </MiniCard>
  );

  // An option of the "Add test" dialog: its name and hint, as the dialog's option buttons show them.
  const addOption = (Icon, key, mark) => (
    <MiniCard icon={Icon} aside={<MiniIcon icon={ChevronRightIcon} />} mark={mark}>
      <MiniRow>
        <MiniText variant="strong">{t(`exam_bank.add_test.${key}`)}</MiniText>
      </MiniRow>
      <MiniRow>
        <MiniText variant="muted">{t(`exam_bank.add_test.${key}_hint`)}</MiniText>
      </MiniRow>
    </MiniCard>
  );

  // A test of the exam (SortableTestItem): the grip, its number and name, its questions, Edit and the bin (with a
  // mouse, the bin shows on the row the mouse is on: here the first). The one marker is on the first grip, the part
  // nobody guesses (Edit says what it does).
  const tests = [
    { key: "t1", name: ex("test_1"), count: 30 },
    { key: "t2", name: ex("test_2"), count: 25 },
  ];
  const testRow = (test, index) => {
    const first = index === 0;
    // The grip as a square icon button: the marker needs room around it.
    const grip = <MiniButton variant="quiet" icon={GripIcon} mark={first} />;
    const actions = (
      <MiniRow className="flex-nowrap">
        <MiniText variant="muted">{t("exam_bank.view.questions_count", { count: test.count })}</MiniText>
        <MiniButton variant="link" icon={PencilIcon}>
          {t("buttons.edit")}
        </MiniButton>
        {(first || !hover) && <MiniIcon icon={TrashIcon} />}
      </MiniRow>
    );
    return (
      <MiniCard key={test.key} icon={grip} aside={wide ? actions : undefined}>
        <MiniRow>
          <MiniBadge tone="blue">{index + 1}</MiniBadge>
          <MiniText variant="strong">{test.name}</MiniText>
        </MiniRow>
        {!wide && <MiniRow className="justify-end">{actions}</MiniRow>}
      </MiniCard>
    );
  };

  return (
    <>
      <HelpIntro>
        <HelpText i18nKey="help.exams.intro" values={{ available }} />
      </HelpIntro>

      <HelpFlow pipeline="exams" current="exams" />

      <HelpSteps>
        <HelpStep
          title={t("help.exams.steps.create.title")}
          illustration={
            <MiniStack>
              <MiniScreen title={t("nav.exam_bank")}>
                <MiniRow className="justify-between gap-y-3">
                  <MiniText variant="title">{t("nav.exam_bank")}</MiniText>
                  <MiniButton variant="primary" icon={PlusIcon} mark="1">
                    {create}
                  </MiniButton>
                </MiniRow>
              </MiniScreen>
              <MiniScreen title={t("exam_bank.create.title")}>
                <MiniRow>
                  <MiniField label={t("exam_bank.form.name")} value={exam} className="flex-1" />
                </MiniRow>
                <MiniRow className="flex-nowrap">
                  <MiniField label={t("exam_bank.form.duration")} value="60" className="flex-1" />
                  <MiniField label={t("exam_bank.form.passing_score")} value="70" className="flex-1" />
                </MiniRow>
                <MiniRow className="justify-end">
                  <MiniButton variant="outline">{t("buttons.cancel")}</MiniButton>
                  <MiniButton variant="primary" mark="2">
                    {t("exam_bank.create.submit")}
                  </MiniButton>
                </MiniRow>
              </MiniScreen>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.exams.steps.create.text" values={{ create }} />
        </HelpStep>

        <HelpStep
          title={t("help.exams.steps.add.title")}
          illustration={
            <MiniStack>
              <MiniScreen title={exam}>{examHeader({ add: "1" })}</MiniScreen>
              <MiniDialog title={t("exam_bank.add_test.title")}>
                {addOption(DocumentPlusIcon, "create_new")}
                {addOption(ListBulletIcon, "select_existing", "2")}
              </MiniDialog>
            </MiniStack>
          }
        >
          <HelpText
            i18nKey="help.exams.steps.add.text"
            values={{
              add,
              create_new: t("exam_bank.add_test.create_new"),
              existing: t("exam_bank.add_test.select_existing"),
            }}
          />
        </HelpStep>

        <HelpStep
          title={t("help.exams.steps.pick.title")}
          illustration={
            <MiniDialog
              title={t("exam_bank.select_tests.title")}
              footer={[
                <MiniButton key="cancel" variant="outline">
                  {t("buttons.cancel")}
                </MiniButton>,
                <MiniButton key="add" variant="primary" mark>
                  {t("exam_bank.select_tests.add_count", { count: 2 })}
                </MiniButton>,
              ]}
            >
              <MiniField icon={MagnifyingGlassIcon} placeholder={t("exam_bank.select_tests.search_placeholder")} />
              <MiniCard icon={<MiniCheck checked />}>
                <MiniText variant="strong">{ex("test_1")}</MiniText>
              </MiniCard>
              <MiniCard icon={<MiniCheck checked />}>
                <MiniText variant="strong">{ex("test_2")}</MiniText>
              </MiniCard>
              <MiniCard icon={<MiniCheck />}>
                <MiniText variant="strong">{ex("test_3")}</MiniText>
              </MiniCard>
            </MiniDialog>
          }
        >
          <HelpText i18nKey="help.exams.steps.pick.text" values={{ add: t("exam_bank.select_tests.add") }} />
        </HelpStep>

        <HelpStep
          title={t("help.exams.steps.order.title")}
          illustration={
            <MiniScreen title={exam}>
              <MiniRow className="justify-between">
                <MiniText variant="strong">{t("exam_bank.view.tests_title")}</MiniText>
                <MiniRow>
                  <MiniIcon icon={ArrowsUpDownIcon} />
                  <MiniText variant="muted">{t("exam_bank.view.drag_hint")}</MiniText>
                </MiniRow>
              </MiniRow>
              {tests.map(testRow)}
            </MiniScreen>
          }
        >
          <HelpText
            i18nKey={hover ? "help.exams.steps.order.text" : "help.exams.steps.order.text_touch"}
            values={{ edit: t("buttons.edit"), bank: t("nav.test_bank") }}
          />
        </HelpStep>

        <HelpStep
          title={t("help.exams.steps.settings.title")}
          illustration={
            <MiniScreen title={exam}>
              {examHeader({ menu: "1" })}
              {draftMenu({ edit: "2" })}
            </MiniScreen>
          }
        >
          <HelpText i18nKey="help.exams.steps.settings.text" values={{ edit: editExam }} />
        </HelpStep>

        <HelpStep
          title={t("help.exams.steps.activate.title")}
          illustration={
            <MiniStack>
              {/* The draft's bar, its activation marked; under it the active exam's bar, where the archive waits. */}
              <MiniCard tone="amber" icon={InformationCircleIcon}>
                <MiniStack>
                  <MiniRow>
                    <MiniText variant="strong">{t("exam_bank.status_actions.state.draft")}</MiniText>
                  </MiniRow>
                  <MiniRow className="gap-y-3">
                    <MiniButton variant="outline" icon={CheckCircleIcon} mark>
                      {activate}
                    </MiniButton>
                    <MiniButton variant="quiet" icon={ArchiveBoxIcon}>
                      {archive}
                    </MiniButton>
                  </MiniRow>
                </MiniStack>
              </MiniCard>
              <MiniCard tone="green" icon={CheckCircleIcon}>
                <MiniStack>
                  <MiniRow>
                    <MiniText variant="strong">{t("exam_bank.status_actions.state.active")}</MiniText>
                  </MiniRow>
                  <MiniRow className="gap-y-3">
                    <MiniButton variant="quiet" icon={ArchiveBoxIcon}>
                      {archive}
                    </MiniButton>
                    <MiniButton variant="quiet" icon={ArrowUturnLeftIcon}>
                      {toDraft}
                    </MiniButton>
                  </MiniRow>
                </MiniStack>
              </MiniCard>
            </MiniStack>
          }
        >
          <HelpText i18nKey="help.exams.steps.activate.text" values={{ activate, archive }} />
        </HelpStep>

        <HelpStep
          title={t("help.exams.steps.documents.title")}
          illustration={
            wordInMenu ? (
              <MiniStack>
                <MiniScreen title={exam}>
                  {examHeader({ menu: "1" })}
                  {draftMenu({ word: "2" })}
                </MiniScreen>
                {wordDialog("3")}
                {wordFiles()}
              </MiniStack>
            ) : (
              <MiniStack>
                <MiniScreen title={exam}>{examHeader({ word: "1" }, { archived: readOnly })}</MiniScreen>
                {wordDialog("2")}
                {wordFiles("3")}
              </MiniStack>
            )
          }
        >
          <p>
            <HelpText
              i18nKey={wordInMenu ? "help.exams.steps.documents.text_phone" : "help.exams.steps.documents.text"}
              values={{ word, dialog: dialogTitle, key: keyFile, student: studentFile }}
            />
          </p>
          <p>
            <HelpText i18nKey="help.exams.steps.documents.settings" />
          </p>
          <p>
            <HelpText
              i18nKey={bilorgButton ? "help.exams.steps.documents.bilorg_button" : "help.exams.steps.documents.bilorg"}
              values={{ bilorg }}
            />
          </p>
          {/* The key and the Excel hold the answers: only for those who may read every question in the bank (the Word
              window says so before it makes the files); keys made before that check may be left out of the list. */}
          <p>
            <HelpText i18nKey="help.exams.steps.documents.answers_hidden" />
          </p>
        </HelpStep>
      </HelpSteps>

      <HelpLegend
        title={t("help.exams.legend_title")}
        rows={[
          { key: "draft", label: t("exam_bank.status.draft"), tone: "amber", text: <HelpText i18nKey="help.exams.legend.draft" /> },
          {
            key: "active",
            label: t("exam_bank.status.active"),
            tone: "green",
            text: <HelpText i18nKey="help.exams.legend.active" values={{ available }} />,
          },
          {
            key: "archived",
            label: t("exam_bank.status.archived"),
            tone: "slate",
            text: <HelpText i18nKey="help.exams.legend.archived" values={{ open: t("exam_bank.view.open_test") }} />,
          },
        ]}
      />

      <HelpTips
        items={[
          t("help.exams.tips.list_status"),
          <HelpText key="delete" i18nKey="help.exams.tips.delete" values={{ delete: deleteExam }} />,
          t("help.exams.tips.attempts"),
          t("help.exams.tips.heading"),
        ]}
      />
    </>
  );
}
